import React, { useEffect, useState, useCallback, useMemo } from "react";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { Card, Row, Col, Button, Dropdown, Table, Nav, Alert } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { getToken, getUserId } from "../services/authorize";
import axiosInstance from "../services/axiosInterceptor";
import { useLoading } from "../contexts/LoadingContext";
import { DAY_NAMES, formatTimeShort } from "./guidanceShared";

const API = process.env.REACT_APP_API;

// หน้าหลักคาบแนะแนวของครูประจำชั้น — ดู slot, เช็คชื่อวันนี้, ประวัติ, สรุปรายนักเรียน
export default function GuidanceAttendance() {
  const currentSkin = localStorage.getItem("skin-mode") ? "dark" : "";
  const [skin, setSkin] = useState(currentSkin);

  const switchSkin = (s) => {
    const tables = document.getElementsByTagName("table");
    for (const table of tables) {
      const theads = table.querySelectorAll("thead");
      if (s === "dark") {
        for (const thead of theads) { thead.classList.remove("table-secondary"); thead.classList.add("table-primary"); }
        table.classList.add("table-dark");
      } else {
        table.classList.remove("table-dark");
        for (const thead of theads) { thead.classList.remove("table-primary"); thead.classList.add("table-secondary"); }
      }
    }
  };
  switchSkin(skin);
  useEffect(() => { switchSkin(skin); }, [skin]);

  const { setLoading } = useLoading();
  const navigate = useNavigate();

  const [yearId, setYearId] = useState(() => localStorage.getItem("yearId") || null);
  const [yearLabel, setYearLabel] = useState("");
  const [yearList, setYearList] = useState([]);
  const [semester, setSemester] = useState("1");
  const [roomId, setRoomId] = useState(null);
  const [noRoom, setNoRoom] = useState(false);
  const [guidanceData, setGuidanceData] = useState([]);
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState([]);
  const [activeTab, setActiveTab] = useState("history"); // history | summary

  const authHeader = useCallback(() => ({
    headers: { authorization: `Bearer ${getToken()}` },
  }), []);

  // ─── Load year list ───────────────────────────────────────────────────────
  useEffect(() => {
    axiosInstance.get(`${API}/getStudyYear`, authHeader()).then((res) => {
      const list = res.data;
      setYearList(list || []);
      let id = localStorage.getItem("yearId");
      if (!id && list.length > 0) { id = list[0].year_id; localStorage.setItem("yearId", id); }
      setYearId(id);
      const sel = list.find((y) => String(y.year_id) === String(id));
      setYearLabel(sel ? `ปีการศึกษา ${sel.year_year}` : "");
    }).catch(() => {});
  }, []);

  const handleSelectYear = (id) => {
    const sel = yearList.find((y) => y.year_id === id);
    if (!sel) return;
    setYearId(id);
    setYearLabel(`ปีการศึกษา ${sel.year_year}`);
    localStorage.setItem("yearId", id);
  };

  // ─── Load room by advisor ─────────────────────────────────────────────────
  useEffect(() => {
    if (!yearId) return;
    setNoRoom(false);
    axiosInstance.get(`${API}/getRoomIdByAdvisorId/${getUserId()}/${yearId}`, authHeader())
      .then((res) => setRoomId(res.data))
      .catch(() => { setNoRoom(true); setRoomId(null); });
  }, [yearId]);

  // ─── Load guidance slots of the room ─────────────────────────────────────
  useEffect(() => {
    if (!roomId) { setGuidanceData([]); return; }
    axiosInstance.get(`${API}/getGuidanceByRoom/${roomId}`, authHeader())
      .then((res) => setGuidanceData(res.data || []))
      .catch(() => setGuidanceData([]));
  }, [roomId]);

  const guidanceSlot = useMemo(
    () => guidanceData.find((g) => String(g.semester) === String(semester)) || null,
    [guidanceData, semester]
  );

  // ─── Load history + summary for the slot ─────────────────────────────────
  useEffect(() => {
    if (!guidanceSlot) { setRecords([]); setSummary([]); return; }
    setLoading(true);
    Promise.all([
      axiosInstance.get(`${API}/getGuidanceTracking/${guidanceSlot.guid_id}`, authHeader()),
      axiosInstance.get(`${API}/getGuidanceSummary/${guidanceSlot.guid_id}`, authHeader()),
    ])
      .then(([trackRes, sumRes]) => {
        setRecords(trackRes.data?.records || []);
        setSummary(sumRes.data || []);
      })
      .catch(() => { setRecords([]); setSummary([]); })
      .finally(() => setLoading(false));
  }, [guidanceSlot, setLoading]);

  const todayYMD = new Date().toISOString().split("T")[0];
  const checkedToday = records.some((r) => r.att_date_raw === todayYMD);

  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">

        {/* Top bar */}
        <div className="d-sm-flex align-items-center justify-content-between mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item"><Link to="#">ครูประจำชั้น</Link></li>
              <li className="breadcrumb-item active">เช็คชื่อคาบแนะแนว</li>
            </ol>
            <h4 className="main-title mb-0">เช็คชื่อคาบแนะแนว</h4>
          </div>
          <div className="d-flex align-items-center gap-2 mt-3 mt-sm-0">
            <div className="btn-group" role="group">
              <button
                type="button"
                className={`btn btn-sm ${semester === "1" ? "btn-primary" : "btn-outline-primary"}`}
                onClick={() => setSemester("1")}
              >
                เทอม 1
              </button>
              <button
                type="button"
                className={`btn btn-sm ${semester === "2" ? "btn-primary" : "btn-outline-primary"}`}
                onClick={() => setSemester("2")}
              >
                เทอม 2
              </button>
            </div>
            <Dropdown>
              <Dropdown.Toggle variant="primary" size="sm">
                {yearLabel || "เลือกปีการศึกษา"}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {yearList.map((y) => (
                  <Dropdown.Item key={y.year_id} onClick={() => handleSelectYear(y.year_id)}>
                    {`ปีการศึกษา ${y.year_year}`}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>

        {noRoom ? (
          <Card className="card-one">
            <Card.Body className="text-center text-muted py-5">
              <i className="ri-calendar-close-line fs-2 d-block mb-2"></i>
              ไม่พบห้องเรียนที่รับผิดชอบในปีการศึกษานี้
            </Card.Body>
          </Card>
        ) : !guidanceSlot ? (
          <Card className="card-one">
            <Card.Body className="text-center text-muted py-5">
              <i className="ri-compass-3-line fs-2 d-block mb-2"></i>
              ยังไม่ได้กำหนดคาบแนะแนวสำหรับเทอมนี้ กรุณาติดต่อฝ่ายจัดตารางสอน
            </Card.Body>
          </Card>
        ) : (
          <Row className="g-3">
            {/* ─── Slot info + check-in button ───────────────────────────── */}
            <Col xs={12}>
              <Card className="card-one">
                <Card.Body className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                  <div>
                    <div className="fw-semibold fs-5 mb-1">
                      <i className="ri-compass-3-line me-2 text-warning"></i>
                      คาบแนะแนว ม.{guidanceSlot.tb_class_c_id}/{guidanceSlot.r_no}
                    </div>
                    <div className="text-muted">
                      วัน{DAY_NAMES[guidanceSlot.teach_day]} คาบ {guidanceSlot.period_order}{" "}
                      ({formatTimeShort(guidanceSlot.period_begin)}–{formatTimeShort(guidanceSlot.period_end)})
                      {" · "}เทอม {guidanceSlot.semester}
                    </div>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    {checkedToday && (
                      <span className="badge bg-success fs-6 fw-normal">
                        <i className="ri-checkbox-circle-line me-1"></i>เช็คชื่อแล้ววันนี้
                      </span>
                    )}
                    <Button
                      variant="success"
                      disabled={checkedToday}
                      onClick={() => navigate(`/advisor/guidance-check/${guidanceSlot.guid_id}`)}
                    >
                      <i className="ri-user-follow-line me-1"></i>เช็คชื่อวันนี้
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            {/* ─── History / Summary tabs ────────────────────────────────── */}
            <Col xs={12}>
              <Card className="card-one">
                <Card.Header>
                  <Nav variant="pills">
                    <Nav.Item>
                      <Nav.Link active={activeTab === "history"} onClick={() => setActiveTab("history")} style={{ cursor: "pointer" }}>
                        ประวัติการเช็คชื่อ
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link active={activeTab === "summary"} onClick={() => setActiveTab("summary")} style={{ cursor: "pointer" }}>
                        สรุปรายนักเรียน
                      </Nav.Link>
                    </Nav.Item>
                  </Nav>
                </Card.Header>
                <Card.Body className="p-3">
                  {activeTab === "history" ? (
                    records.length === 0 ? (
                      <Alert variant="light" className="text-center text-muted mb-0">
                        ยังไม่มีประวัติการเช็คชื่อ
                      </Alert>
                    ) : (
                      <Table responsive hover className="mb-0">
                        <thead>
                          <tr>
                            <th className="text-center" style={{ width: 50 }}>#</th>
                            <th>วันที่</th>
                            <th>กิจกรรม/หัวข้อ</th>
                            <th className="text-center">มา</th>
                            <th className="text-center">สาย</th>
                            <th className="text-center">ลา</th>
                            <th className="text-center">ขาด</th>
                            <th className="text-center">รวม</th>
                            <th className="text-center" style={{ width: 90 }}>แก้ไข</th>
                          </tr>
                        </thead>
                        <tbody>
                          {records.map((r, i) => (
                            <tr key={r.gtr_id}>
                              <td className="text-center">{records.length - i}</td>
                              <td>{r.att_day_th} {r.att_date_th}</td>
                              <td>{r.gtr_topic}</td>
                              <td className="text-center text-success">{r.presents ?? 0}</td>
                              <td className="text-center text-warning">{r.lates ?? 0}</td>
                              <td className="text-center text-info">{r.leaves ?? 0}</td>
                              <td className="text-center text-danger">{r.absents ?? 0}</td>
                              <td className="text-center">{r.total_students ?? 0}</td>
                              <td className="text-center">
                                <Link
                                  to={`/advisor/guidance-edit/${guidanceSlot.guid_id}/${r.att_date_raw}`}
                                  className="btn btn-outline-primary btn-sm"
                                >
                                  <i className="ri-edit-line"></i>
                                </Link>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    )
                  ) : (
                    summary.length === 0 ? (
                      <Alert variant="light" className="text-center text-muted mb-0">
                        ไม่พบข้อมูลนักเรียน
                      </Alert>
                    ) : (
                      <Table responsive hover className="mb-0">
                        <thead>
                          <tr>
                            <th className="text-center" style={{ width: 50 }}>เลขที่</th>
                            <th className="text-center" style={{ width: 110 }}>เลขประจำตัว</th>
                            <th>ชื่อ-สกุล</th>
                            <th className="text-center">เช็คแล้ว (ครั้ง)</th>
                            <th className="text-center">มา</th>
                            <th className="text-center">สาย</th>
                            <th className="text-center">ลา</th>
                            <th className="text-center">ขาด</th>
                          </tr>
                        </thead>
                        <tbody>
                          {summary.map((s, i) => (
                            <tr key={s.study_id}>
                              <td className="text-center">{i + 1}</td>
                              <td className="text-center">{s.st_idnumber}</td>
                              <td>{s.st_name}</td>
                              <td className="text-center">{s.total_sessions ?? 0}</td>
                              <td className="text-center text-success">{s.presents ?? 0}</td>
                              <td className="text-center text-warning">{s.lates ?? 0}</td>
                              <td className="text-center text-info">{s.leaves ?? 0}</td>
                              <td className="text-center text-danger">{s.absents ?? 0}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    )
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        <Footer />
      </div>
    </React.Fragment>
  );
}
